import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useSession } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/back-navigation";
import { ScrollNavigation } from "@/components/scroll-navigation";
import { Mail, Phone, Building2, CheckCircle, XCircle, MessageSquare, Calendar } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface DemoEnquiry {
  id: number;
  full_name: string;
  email: string;
  organization_name: string;
  number_of_homes?: number | null;
  message?: string | null;
  data_consent: boolean;
  marketing_consent: boolean;
  contacted: boolean;
  contacted_by?: number | null;
  contacted_at?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export function EnquiriesPage() {
  const { session } = useSession();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "new" | "contacted">("all");
  const [selectedEnquiry, setSelectedEnquiry] = useState<DemoEnquiry | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [contactNotes, setContactNotes] = useState("");

  const { data: enquiries = [], isLoading } = useQuery<DemoEnquiry[]>({
    queryKey: ["/api/demo-enquiries"],
  });

  const updateContactMutation = useMutation({
    mutationFn: async (data: { id: number; contacted: boolean; notes?: string }) => {
      return await apiRequest("PATCH", `/api/demo-enquiries/${data.id}/contact`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/demo-enquiries"] });
      toast({
        title: "Success",
        description: "Contact status updated successfully",
      });
      setShowDetailsDialog(false);
      setContactNotes("");
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update contact status",
        variant: "destructive",
      });
    },
  });

  const filteredEnquiries = enquiries.filter((enquiry) => {
    const matchesSearch = 
      enquiry.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.organization_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === "all" ||
      (statusFilter === "new" && !enquiry.contacted) ||
      (statusFilter === "contacted" && enquiry.contacted);

    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = (enquiry: DemoEnquiry) => {
    setSelectedEnquiry(enquiry);
    setContactNotes(enquiry.notes || "");
    setShowDetailsDialog(true);
  };

  const handleMarkContacted = () => {
    if (!selectedEnquiry) return;
    updateContactMutation.mutate({
      id: selectedEnquiry.id,
      contacted: true,
      notes: contactNotes,
    });
  };

  const handleMarkUncontacted = () => {
    if (!selectedEnquiry) return;
    updateContactMutation.mutate({
      id: selectedEnquiry.id,
      contacted: false,
      notes: contactNotes,
    });
  };

  const newEnquiriesCount = enquiries.filter(e => !e.contacted).length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <PageHeader 
        title="Demo Enquiries" 
        subtitle={`Manage and track demo requests from potential customers`}
        onBack={() => window.history.back()}
      />

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Demo Enquiries</CardTitle>
                <CardDescription>
                  {newEnquiriesCount > 0 && (
                    <span className="text-orange-600 dark:text-orange-400 font-medium">
                      {newEnquiriesCount} new enquir{newEnquiriesCount === 1 ? 'y' : 'ies'} awaiting contact
                    </span>
                  )}
                  {newEnquiriesCount === 0 && "All enquiries have been contacted"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by name, email, or organization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="input-search-enquiries"
                />
              </div>
              <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                <SelectTrigger className="w-[180px]" data-testid="select-filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Enquiries</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isLoading ? (
              <div className="text-center py-8 text-slate-500">Loading enquiries...</div>
            ) : filteredEnquiries.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No enquiries found
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Homes</TableHead>
                      <TableHead>Consent</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEnquiries.map((enquiry) => (
                      <TableRow key={enquiry.id} data-testid={`row-enquiry-${enquiry.id}`}>
                        <TableCell>
                          {enquiry.contacted ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Contacted
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                              <XCircle className="w-3 h-3 mr-1" />
                              New
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-medium" data-testid={`text-name-${enquiry.id}`}>
                          {enquiry.full_name}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            {enquiry.organization_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">{enquiry.email}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {enquiry.number_of_homes ? (
                            <Badge variant="outline">{enquiry.number_of_homes} homes</Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">Not specified</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {enquiry.data_consent && (
                              <Badge variant="outline" className="text-xs">Data</Badge>
                            )}
                            {enquiry.marketing_consent && (
                              <Badge variant="outline" className="text-xs">Marketing</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {new Date(enquiry.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewDetails(enquiry)}
                            data-testid={`button-view-details-${enquiry.id}`}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Enquiry Details</DialogTitle>
            <DialogDescription>View and manage enquiry information</DialogDescription>
          </DialogHeader>
          
          {selectedEnquiry && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-500">Full Name</Label>
                  <p className="mt-1 text-slate-900 dark:text-slate-100">{selectedEnquiry.full_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-500">Email</Label>
                  <p className="mt-1 text-slate-900 dark:text-slate-100">{selectedEnquiry.email}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-500">Organization</Label>
                  <p className="mt-1 text-slate-900 dark:text-slate-100">{selectedEnquiry.organization_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-500">Number of Homes</Label>
                  <p className="mt-1 text-slate-900 dark:text-slate-100">
                    {selectedEnquiry.number_of_homes || "Not specified"}
                  </p>
                </div>
              </div>

              {selectedEnquiry.message && (
                <div>
                  <Label className="text-sm font-medium text-slate-500">Message</Label>
                  <p className="mt-1 text-slate-900 dark:text-slate-100 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-3 rounded-md">
                    {selectedEnquiry.message}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-500">Data Storage Consent</Label>
                  <div className="mt-1">
                    {selectedEnquiry.data_consent ? (
                      <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Granted
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        <XCircle className="w-3 h-3 mr-1" />
                        Not Granted
                      </Badge>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-500">Marketing Consent</Label>
                  <div className="mt-1">
                    {selectedEnquiry.marketing_consent ? (
                      <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Granted
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        <XCircle className="w-3 h-3 mr-1" />
                        Not Granted
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-500">Contact Status</Label>
                <div className="mt-2">
                  {selectedEnquiry.contacted ? (
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-medium">Contacted</span>
                      </div>
                      {selectedEnquiry.contacted_at && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {new Date(selectedEnquiry.contacted_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-md">
                      <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                        <XCircle className="w-4 h-4" />
                        <span className="font-medium">Not Yet Contacted</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="contact-notes">Contact Notes</Label>
                <Textarea
                  id="contact-notes"
                  placeholder="Add notes about this contact..."
                  value={contactNotes}
                  onChange={(e) => setContactNotes(e.target.value)}
                  rows={4}
                  className="mt-1"
                  data-testid="textarea-contact-notes"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowDetailsDialog(false)}
                  data-testid="button-cancel"
                >
                  Close
                </Button>
                {selectedEnquiry.contacted ? (
                  <Button
                    variant="outline"
                    onClick={handleMarkUncontacted}
                    disabled={updateContactMutation.isPending}
                    data-testid="button-mark-uncontacted"
                  >
                    {updateContactMutation.isPending ? "Updating..." : "Mark as Uncontacted"}
                  </Button>
                ) : (
                  <Button
                    onClick={handleMarkContacted}
                    disabled={updateContactMutation.isPending}
                    data-testid="button-mark-contacted"
                  >
                    {updateContactMutation.isPending ? "Updating..." : "Mark as Contacted"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ScrollNavigation />
    </div>
  );
}
